import { useMemo } from 'react';

import type * as api from '@gitbook/api-client';
import * as analytics from '@gitbook/app-analytics/client';
import { assertIsDefined, assertNever } from '@gitbook/util-ts';

import { apiClient } from '../../../state';
import { flattenSpacesInOrgAllContent } from '../../Organization';
import { getAvailableSiteSpaces } from '../../Site';
import { SpaceListSelector, type SpaceListSelectorProps } from '../SpaceListSelector';
import type { SpaceCreationOnCreateSpace, SpaceCreationTarget } from './types';

export interface PickExistingSpaceProps
    extends Omit<SpaceListSelectorProps, 'actionLabel' | 'onChange' | 'spaces'> {
    organizationId: string;
    onCreateSpace: SpaceCreationOnCreateSpace;
    state: UsePickExistingSpaceState;
}

export interface UsePickExistingSpaceProps {
    target: SpaceCreationTarget;
    orgAllContent: api.OrganizationAllContent;
}

export interface UsePickExistingSpaceState {
    action: 'use' | 'duplicate';
    spaces: api.Space[];
}

export function usePickExistingSpaceState({
    target,
    orgAllContent,
}: UsePickExistingSpaceProps): UsePickExistingSpaceState {
    const spaces = useMemo(() => {
        switch (target.type) {
            case 'siteVariant':
            case 'siteSection': {
                const orgAllSite = orgAllContent.sites.find(
                    (site) => target.props.site.id === site.site.id
                );
                assertIsDefined(orgAllSite, 'Site should be defined in orgAllContent');
                return getAvailableSiteSpaces(orgAllSite.structure, orgAllContent);
            }
            case 'space':
            case 'site': {
                return flattenSpacesInOrgAllContent(orgAllContent);
            }
            default:
                assertNever(target);
        }
    }, [orgAllContent, target]);

    const action = (() => {
        switch (target.type) {
            case 'siteSection':
            case 'siteVariant':
            case 'site':
                return 'use';
            case 'space':
                return 'duplicate';
            default:
                assertNever(target);
        }
    })();

    return { spaces, action };
}

export function PickExistingSpace(props: PickExistingSpaceProps) {
    const { organizationId, state, onCreateSpace, ...rest } = props;

    return (
        <SpaceListSelector
            spaces={state.spaces}
            actionLabel={(() => {
                switch (state.action) {
                    case 'use':
                        return 'Use as content';
                    case 'duplicate':
                        return 'Duplicate';
                    default:
                        assertNever(state.action);
                }
            })()}
            onChange={async (space) => {
                switch (state.action) {
                    case 'use':
                        await onCreateSpace(() => space);
                        break;
                    case 'duplicate': {
                        await onCreateSpace(async ({ collectionId }) => {
                            const { data: duplicatedSpace } = await apiClient.spaces.duplicateSpace(
                                space.id
                            );

                            // We log the event here as it's a different flow than the other commands
                            analytics.logEvent(
                                analytics.createSpaceEvent(duplicatedSpace, {
                                    type: 'space_create',
                                    source: 'duplicate',
                                })
                            );

                            if (collectionId !== duplicatedSpace.parent) {
                                const { data: movedSpace } = await apiClient.spaces.moveSpace(
                                    duplicatedSpace.id,
                                    {
                                        parent: collectionId,
                                    }
                                );
                                return movedSpace;
                            }

                            return duplicatedSpace;
                        });
                        break;
                    }
                    default:
                        assertNever(state.action);
                }
            }}
            {...rest}
        />
    );
}
